import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const AppToastContext = createContext(null);

const DEFAULT_TITLES = {
  error: 'Erreur',
  success: 'Succès',
  info: 'Information',
};

/**
 * App-wide feedback toasts (errors, success) — fixed top-right (below header)
 * so they stay visible while scrolling long forms.
 */
export function AppToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timeoutsRef = useRef(new Set());

  useEffect(() => () => {
    for (const timeoutId of timeoutsRef.current) {
      clearTimeout(timeoutId);
    }
    timeoutsRef.current.clear();
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(({ type = 'info', message, title, duration = 6000 }) => {
    if (!message) return;
    const id = ++idRef.current;
    setToasts((prev) => [...prev, {
      id,
      type,
      title: title || DEFAULT_TITLES[type] || DEFAULT_TITLES.info,
      message,
    }]);
    const timeoutId = setTimeout(() => {
      removeToast(id);
      timeoutsRef.current.delete(timeoutId);
    }, duration);
    timeoutsRef.current.add(timeoutId);
  }, [removeToast]);

  const showError = useCallback(
    (message, title) => showToast({ type: 'error', message, title, duration: 8000 }),
    [showToast],
  );

  const showSuccess = useCallback(
    (message, title) => showToast({ type: 'success', message, title }),
    [showToast],
  );

  const showInfo = useCallback(
    (message, title) => showToast({ type: 'info', message, title }),
    [showToast],
  );

  const value = {
    toasts,
    removeToast,
    showToast,
    showError,
    showSuccess,
    showInfo,
  };

  return (
    <AppToastContext.Provider value={value}>
      {children}
    </AppToastContext.Provider>
  );
}

export function useAppToast() {
  const context = useContext(AppToastContext);
  if (!context) {
    return {
      toasts: [],
      removeToast: () => {},
      showToast: () => {},
      showError: () => {},
      showSuccess: () => {},
      showInfo: () => {},
    };
  }
  return context;
}
